import React, { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { IInfluencer, IPlatformStats, IPriceCard } from '../../../redux/buzzbridge/types';
import { Colors } from '../../../constants/theme';
import {
    CREATOR_METRIC_GOLD,
    CREATOR_METRIC_ON_VARIANT,
    CREATOR_METRIC_RADIUS_BTN,
    CREATOR_METRIC_RADIUS_TILE,
    CREATOR_METRIC_SURFACE_HI,
    CREATOR_METRIC_SURFACE_LOW,
} from './creatorProfileMetricTokens';
import { RateCardBuilderModal } from './RateCardBuilderModal';
import {
    PLATFORM_DELIVERABLES,
    RATE_CARD_PLATFORMS,
    type RateCardPlatformId,
    getStoredDeliverableRate,
} from '../utils/rateCardPlatformConfig';
import { formatFollowersForInsight } from '../utils/rateCardSuggestions';
import { InstagramGlyph, TiktokGlyph, YoutubeGlyph } from '../SocialPlatformSvgs';

type ThemeStyle = 'light' | 'dark';

interface EditProfileRateCardTabProps {
    formData: Partial<IInfluencer>;
    handleInputChange: (field: keyof IInfluencer, value: unknown) => void;
    themeTokens: Colors;
    themeStyle: ThemeStyle;
    saving?: boolean;
}

function platformGlyph(id: RateCardPlatformId) {
    if (id === 'instagram') return <InstagramGlyph sx={{ width: 24, height: 24 }} color="#fff" />;
    if (id === 'tiktok') return <TiktokGlyph sx={{ width: 24, height: 24 }} color="#fff" />;
    return <YoutubeGlyph sx={{ width: 24, height: 24 }} color="#fff" />;
}

function countConfiguredRates(stats: IPlatformStats | undefined, platformId: RateCardPlatformId): number {
    if (!stats?.pricing) return 0;
    return PLATFORM_DELIVERABLES[platformId].filter(d => getStoredDeliverableRate(stats.pricing, d.key) > 0).length;
}

export const EditProfileRateCardTab: React.FC<EditProfileRateCardTabProps> = ({
    formData,
    handleInputChange,
    themeTokens: t,
    saving,
}) => {
    const [builderOpen, setBuilderOpen] = useState(false);
    const [builderPlatform, setBuilderPlatform] = useState<RateCardPlatformId>('instagram');

    const available = useMemo(() => {
        return RATE_CARD_PLATFORMS.filter(p => {
            const stats = formData[p.id] as IPlatformStats | undefined;
            return Boolean(stats?.username || stats?.followers != null);
        });
    }, [formData]);

    const openBuilder = (platformId: RateCardPlatformId) => {
        setBuilderPlatform(platformId);
        setBuilderOpen(true);
    };

    const handleSaveRates = (platform: RateCardPlatformId, pricing: Partial<IPriceCard>) => {
        const stats = formData[platform] as IPlatformStats | undefined;
        handleInputChange(platform, {
            ...stats,
            pricing: {
                ...stats?.pricing,
                ...pricing,
            },
        });
        setBuilderOpen(false);
    };

    return (
        <Box component="section">
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        bgcolor: 'rgba(255,201,101,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <PaymentsOutlinedIcon sx={{ color: CREATOR_METRIC_GOLD, fontSize: 28 }} />
                </Box>
                <Box>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: '1.15rem', md: '1.35rem' },
                            color: t.primary_white,
                            mb: 0.5,
                        }}>
                        Rate card
                    </Typography>
                    <Typography sx={{ color: CREATOR_METRIC_ON_VARIANT, fontSize: 14, maxWidth: 520, lineHeight: 1.5 }}>
                        Set per-deliverable pricing for each connected platform. Suggested rates use your live follower
                        counts.
                    </Typography>
                </Box>
            </Box>

            {available.length === 0 ? (
                <Box
                    sx={{
                        p: 3,
                        borderRadius: CREATOR_METRIC_RADIUS_TILE,
                        bgcolor: CREATOR_METRIC_SURFACE_LOW,
                        border: '1px dashed rgba(72,72,71,0.4)',
                    }}>
                    <Typography sx={{ color: CREATOR_METRIC_ON_VARIANT, fontSize: 14 }}>
                        Connect a social platform under Social Platforms first, then return here to set your rates.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {available.map(p => {
                        const stats = formData[p.id] as IPlatformStats | undefined;
                        const configured = countConfiguredRates(stats, p.id);
                        const total = PLATFORM_DELIVERABLES[p.id].length;
                        return (
                            <Box
                                key={p.id}
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    p: 2.5,
                                    borderRadius: CREATOR_METRIC_RADIUS_TILE,
                                    bgcolor: CREATOR_METRIC_SURFACE_LOW,
                                    border: '1px solid rgba(72,72,71,0.25)',
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: '50%',
                                            bgcolor: CREATOR_METRIC_SURFACE_HI,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        {platformGlyph(p.id)}
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: '#fff',
                                                fontSize: 15,
                                            }}>
                                            {p.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: CREATOR_METRIC_ON_VARIANT }}>
                                            {formatFollowersForInsight(stats?.followers ?? 0)} followers · {configured}/
                                            {total} rates set
                                        </Typography>
                                    </Box>
                                </Box>
                                <Button
                                    onClick={() => openBuilder(p.id)}
                                    sx={{
                                        borderRadius: CREATOR_METRIC_RADIUS_BTN,
                                        px: 3,
                                        py: 1,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 700,
                                        color: '#392700',
                                        background: `linear-gradient(90deg, ${CREATOR_METRIC_GOLD}, #feb700)`,
                                        '&:hover': {
                                            background: `linear-gradient(90deg, #ffd080, #feb700)`,
                                        },
                                    }}>
                                    {configured > 0 ? 'Edit rates' : 'Set rates'}
                                </Button>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <RateCardBuilderModal
                open={builderOpen}
                onClose={() => setBuilderOpen(false)}
                influencer={formData as IInfluencer}
                initialPlatform={builderPlatform}
                onSave={handleSaveRates}
                saving={saving}
            />
        </Box>
    );
};
